/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { GearIcon } from '@/shared/ui/radix-icons';
import { PageTitleWithAreaIntro, Text } from '@/shared/ui';
import { useTranslation } from 'react-i18next';
import type { ReactNode } from 'react';

interface SettingsHeaderProps {
  title: string;
  agencyName: string;
  isMobile: boolean;
  /** The settings search, drawn beside the agency name. */
  search?: ReactNode;
}

/**
 * Settings page header showing the section title with area intro and the
 * active agency name.
 * @param props - Component props.
 * @param props.title - The current section title.
 * @param props.agencyName - Name of the active agency.
 * @param props.isMobile - Whether to render the stacked mobile layout.
 * @param props.search - The settings search, when the page offers one.
 * @returns The settings header element.
 */
export function SettingsHeader({ title, agencyName, isMobile, search }: SettingsHeaderProps) {
  const { t } = useTranslation('features/agency-settings');

  return (
    <div
      data-testid="settings-header"
      data-layout={isMobile ? 'stacked' : 'row'}
      style={{
        padding: 'var(--space-4) var(--space-5)',
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--border-subtle)',
        background: 'var(--surface-primary)',
        boxShadow: 'var(--shadow-md)',
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'flex-start' : 'center',
        justifyContent: 'space-between',
        gap: 'var(--space-2)',
        fontFamily: 'var(--font-family)',
      }}
    >
      <PageTitleWithAreaIntro
        icon={<GearIcon width={22} height={22} />}
        intro={{
          label: t('intro.label'),
          body: t('intro.body'),
          testIdPrefix: 'settings',
        }}
      >
        {title}
      </PageTitleWithAreaIntro>
      <div
        className="flex items-center gap-(--space-4)"
        style={{
          width: isMobile ? '100%' : undefined,
          flexWrap: isMobile ? 'wrap' : 'nowrap',
          justifyContent: 'flex-end',
        }}
      >
        {search}
        <Text component="span" style={{ fontSize: 'var(--text-body-sm)', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
          {agencyName}
        </Text>
      </div>
    </div>
  );
}
